import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtCVE, fmtDate, PAYMENT_METHODS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, CreditCard, CircleNotch, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Payments() {
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ invoice_id: "", amount: "", method: "transferencia", date: "", reference: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, inv] = await Promise.all([
        api.get("/payments"),
        api.get("/invoices", { params: { limit: 200 } }),
      ]);
      setItems(p.data.items);
      setInvoices(inv.data.items.filter((i) => i.balance > 0 && i.status !== "anulada"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setPay({ invoice_id: "", amount: "", method: "transferencia", date: new Date().toISOString().slice(0, 10), reference: "" });
    setOpen(true);
  };

  const onInvoice = (invoice_id) => {
    const inv = invoices.find((i) => i.id === invoice_id);
    setPay({ ...pay, invoice_id, amount: inv ? inv.balance : "" });
  };

  const save = async () => {
    if (!pay.invoice_id) return toast.error("Selecione um documento");
    if (!pay.amount || Number(pay.amount) <= 0) return toast.error("Valor inválido");
    setBusy(true);
    try {
      await api.post("/payments", { ...pay, amount: Number(pay.amount), date: pay.date || null });
      toast.success("Pagamento registado");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm("Eliminar este pagamento?")) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tesouraria</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Pagamentos</h1>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="new-payment-btn">
          <Plus size={18} className="mr-1.5" /> Novo Pagamento
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <CreditCard size={40} className="text-muted-foreground" />
            <p className="text-muted-foreground">Ainda não há pagamentos.</p>
            <Button variant="outline" className="rounded-full" onClick={openNew}>Registar primeiro pagamento</Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Documento</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="hidden md:table-cell">Método</TableHead>
                <TableHead className="hidden md:table-cell">Data</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} className="stagger-item" data-testid={`payment-row-${p.id}`}>
                  <TableCell className="font-mono text-xs font-medium">{p.invoice_number}</TableCell>
                  <TableCell>{p.client_name}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{PAYMENT_METHODS[p.method] || p.method}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{fmtDate(p.date)}</TableCell>
                  <TableCell className="text-right font-medium text-emerald-600">{fmtCVE(p.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(p)} data-testid={`delete-payment-${p.id}`}>
                      <Trash size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="global-payment-dialog">
          <DialogHeader><DialogTitle>Novo pagamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Documento *</Label>
              <Select value={pay.invoice_id} onValueChange={onInvoice}>
                <SelectTrigger className="w-full min-w-0 [&>span]:truncate" data-testid="payment-invoice"><SelectValue placeholder="Selecionar documento com saldo" /></SelectTrigger>
                <SelectContent>
                  {invoices.length === 0 && <div className="px-2 py-2 text-sm text-muted-foreground">Sem documentos em dívida</div>}
                  {invoices.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.number} — {i.client_name} ({fmtCVE(i.balance)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Valor (CVE)</Label>
              <Input type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="global-payment-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Método</Label>
                <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                  <SelectTrigger data-testid="global-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAYMENT_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Data</Label>
                <Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="global-payment-date" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Referência</Label>
              <Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} data-testid="global-payment-reference" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-global-payment-btn">
              {busy ? <CircleNotch className="animate-spin" /> : "Registar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
